// src/pages/customer/components/details/tabs/CustomerClubsTab.jsx
import { Badge, Button } from "react-bootstrap";
import { formatDate, formatDateTime, formatInReal } from "../../../../../commons/util/HallAppUtil";

import AppLoading from "../../../../../commons/AppLoading";
import { useEffect } from "react";

/**
 * Status possíveis:
 * PENDING, SENT, ACTIVE, PAUSED, CANCELED, EXPIRED, FAILED
 */
const clubStatusLabel = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "PENDING":
      return "Pendente";
    case "SENT":
      return "Convite enviado";
    case "ACTIVE":
      return "Ativo";
    case "PAUSED":
      return "Pausado";
    case "CANCELED":
      return "Cancelado";
    case "EXPIRED":
      return "Expirado";
    case "FAILED":
      return "Falhou";
    default:
      return "—";
  }
};

const clubStatusVariant = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "ACTIVE":
      return "success";
    case "PAUSED":
      return "warning";
    case "CANCELED":
      return "secondary";
    case "EXPIRED":
      return "dark";
    case "FAILED":
      return "danger";
    case "PENDING":
      return "info";
    case "SENT":
      return "primary";
    default:
      return "dark";
  }
};

const formatFrequencyPtBr = (freq) => {
  const f = String(freq || "").toUpperCase();
  switch (f) {
    case "MONTHLY":
      return "Mensal";
    case "YEARLY":
      return "Anual";
    case "WEEKLY":
      return "Semanal";
    case "DAILY":
      return "Diário";
    default:
      return freq || null;
  }
};

const formatBillingRulePtBr = (rule, fixedDayOfMonth) => {
  const r = String(rule || "").toUpperCase();
  switch (r) {
    case "ON_SUBSCRIPTION_DAY":
      return "No dia da assinatura";
    case "FIXED_DAY_OF_MONTH":
      return fixedDayOfMonth ? `Todo dia ${fixedDayOfMonth}` : "Dia fixo do mês";
    default:
      return rule || null;
  }
};

const SafeLine = ({ show, children }) => (show ? <span>{children}</span> : null);

export default function CustomerClubsTab({ customerId, show, isLoading, clubs, refresh }) {
  useEffect(() => {
    if (!show) return;
    refresh?.();
  }, [show, refresh]);

  if (isLoading) {
    return (
      <div className="mt-3">
        <AppLoading />
      </div>
    );
  }

  if (!clubs || clubs.length === 0) {
    return (
      <div className="hc-cd-panel">
        <div className="hc-cd-panel__title">Assinaturas</div>
        <div className="hc-cd-empty">
          <div className="hc-cd-empty__title">Nenhuma assinatura encontrada</div>
          <div className="hc-cd-empty__subtitle">Esse cliente ainda não possui assinaturas.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hc-cd-panel">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="hc-cd-panel__title mb-0">Assinaturas</div>

        <Button size="sm" variant="outline-dark" onClick={refresh}>
          Atualizar
        </Button>
      </div>

      <div className="hc-cd-clubs">
        {clubs.map((it, idx) => {
          const key = it.subscriptionId ?? it.id ?? idx;

          const statusText = clubStatusLabel(it.status);
          const statusBg = clubStatusVariant(it.status);

          const freqText = formatFrequencyPtBr(it.planFrequency);
          const ruleText = formatBillingRulePtBr(it.planBillingRule, it.planFixedDayOfMonth);

          const amountText =
            it.planAmount != null ? formatInReal(Number(it.planAmount) || 0) : null;

          return (
            <div key={key} className="hc-cd-clubCard">
              {/* TOP */}
              <div className="hc-cd-clubCard__top">
                <div className="hc-cd-clubCard__title text-truncate">
                  {it.planName || it.clubPlanName || "Plano"}
                </div>

                <Badge bg={statusBg} pill className="hc-cd-clubCard__status">
                  {statusText}
                </Badge>
              </div>

              {/* MAIN META */}
              <div className="hc-cd-clubCard__meta">
                <SafeLine show={!!amountText}>
                  💰 Valor: <strong>{amountText}</strong>
                </SafeLine>

                <SafeLine show={!!freqText}>🔁 Frequência: {freqText}</SafeLine>

              </div>

              {/* DATES */}
              <div className="hc-cd-clubCard__dates">
                <SafeLine show={!!it.invitedDt}>📨 Convidado em: {formatDateTime(it.invitedDt)}</SafeLine>
                <SafeLine show={!!it.linkCreatedDt}>🔗 Link gerado em: {formatDateTime(it.linkCreatedDt)}</SafeLine>
                <SafeLine show={!!it.subscribedDt}>✅ Assinado em: {formatDateTime(it.subscribedDt)}</SafeLine>
                <SafeLine show={!!it.startDt}>🚀 Início: {formatDateTime(it.startDt)}</SafeLine>
                <SafeLine show={!!it.endDt}>🏁 Fim: {formatDateTime(it.endDt)}</SafeLine>
                <SafeLine show={!!it.canceledDt}>❌ Cancelado em: {formatDateTime(it.canceledDt)}</SafeLine>
                <SafeLine show={!!it.inviteExpiresDt}>
                  ⏳ Expira em: {formatDateTime(it.inviteExpiresDt)}
                </SafeLine>
              </div>

              {/* ACTION LINKS (opcional) */}
              {it.checkoutUrl ? (
                <div className="hc-cd-clubCard__actions">
                  <a className="btn btn-sm btn-outline-dark" href={it.checkoutUrl} target="_blank" rel="noreferrer">
                    Abrir checkout
                  </a>
                </div>
              ) : null}

              {/* NOTES / REASON */}
              {it.cancelReason ? (
                <div className="hc-cd-clubCard__notes">
                  <strong>Motivo:</strong> {it.cancelReason}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
